/** Dev21 — Video. Responsive teaser-film player with click-to-play poster. */

import React from 'react';
import { motion } from 'motion/react';
import { Play } from 'lucide-react';
import { Section, SectionHeading, Reveal } from '../components/primitives';
import { DEV21_EASE } from '../theme';
import type { Dev21Content } from '../content';

export function VideoSection({
  content,
}: {
  content: Dev21Content['video'];
}) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = React.useState(false);

  const handlePlay = () => {
    setPlaying(true);
    // Defer so the <video> is mounted/visible before playback starts.
    requestAnimationFrame(() => videoRef.current?.play());
  };

  return (
    <Section id="video" tone="ink">
      <SectionHeading
        label={content.label}
        title={content.title}
        intro={content.description}
        invert
      />

      <Reveal className="mt-12">
        <div className="relative mx-auto aspect-video w-full max-w-4xl overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_90px_-40px_rgba(0,0,0,0.8)]">
          {playing ? (
            <video
              ref={videoRef}
              src={content.src}
              poster={content.poster}
              controls
              playsInline
              className="h-full w-full bg-black object-cover"
            />
          ) : (
            <button
              type="button"
              onClick={handlePlay}
              aria-label="Play teaser video"
              className="group absolute inset-0 h-full w-full"
            >
              <img
                src={content.poster}
                alt=""
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 transition-colors group-hover:bg-black/30" />
              <span className="absolute inset-0 flex items-center justify-center">
                <motion.span
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3, ease: DEV21_EASE }}
                  className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--d21-accent)] text-white shadow-2xl"
                >
                  {/* Pulsing ring */}
                  <motion.span
                    aria-hidden
                    animate={{ scale: [1, 1.6], opacity: [0.5, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
                    className="absolute h-20 w-20 rounded-full border border-[var(--d21-accent)]"
                  />
                  <Play size={28} className="ml-1 fill-white" />
                </motion.span>
              </span>
            </button>
          )}
        </div>
      </Reveal>
    </Section>
  );
}
